import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScopedSearchParams } from '@/hooks/useUrlState';
import { clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Dialog as ScrollableDialog, ScrollableDialogContent, DialogHeader as ScrollableDialogHeader, DialogTitle as ScrollableDialogTitle, DialogDescription as ScrollableDialogDescription, DialogFooter as ScrollableDialogFooter, DialogTrigger as ScrollableDialogTrigger } from '@/components/ui/scrollable-dialog';
import { Badge } from '@/components/ui/badge';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useWorkspaceScopeId } from '@/hooks/useWorkspaceScopeId';
import { useCommerceSyncEvents } from '@/hooks/useCommerceRealtime';
import { shopifyApi, formatCurrency } from '@/lib/api/services/shopify';
import { API_CONFIG } from '@/lib/api/config';
import { toast } from '@/hooks/use-toast';
import { useCatalogInfo } from '@/hooks/useCommerce';
import { Search, X, Trash2, Loader2, ArrowLeft, RefreshCw, Webhook, Pencil, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { ShopifyOrders } from '@/components/shopify/ShopifyOrders';
import { ShopifyProducts } from '@/components/shopify/ShopifyProducts';
import { CreateShopifyProductDialog } from '@/components/shopify/CreateShopifyProductDialog';
import { ShopifyCustomers } from '@/components/shopify/ShopifyCustomers';
import { ShopifyCheckouts } from '@/components/shopify/ShopifyCheckouts';
import { ApiKeysManager } from '@/components/shopify/ApiKeysManager';
import { ShopifyConfiguration } from '@/components/shopify/ShopifyConfiguration';
import { ShopifyPlaybooks } from '@/components/shopify/ShopifyPlaybooks';
import { useAuth } from '@/components/AuthProvider';
import { friendlyMessage, showApiError } from '@/lib/errors';

export default function ShopifyPage({ variant = 'page' }: { variant?: 'page' | 'drawer' } = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const organizationId = user?.organization_id || null;
  const { isLoading: accountsLoading } = useBusinessAccountContext();
  const workspaceScopeId = useWorkspaceScopeId();
  const { data: catalogInfo } = useCatalogInfo();
  const [localProducts, setLocalProducts] = useState<any[]>([]);
  const [localCheckouts, setLocalCheckouts] = useState<any[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{ shop?: string; currency?: string; installedAt?: string; businessId?: string | null } | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(true);
  const [verification, setVerification] = useState<
    'pending' | 'verified' | 'unverified' | null
  >(null);
  const connectionCheckSeq = React.useRef(0);
  const [shopCurrency, setShopCurrency] = useState<string>('USD');
  const [connectionMethod, setConnectionMethod] = useState<'token' | 'credentials'>('credentials');
  const [manualForm, setManualForm] = useState({
    shop: '',
    accessToken: '',
    clientId: '',
    clientSecret: '',
  });
  const [manualLoading, setManualLoading] = useState(false);
  const [createProductOpen, setCreateProductOpen] = useState(false);
  const [createProductForm, setCreateProductForm] = useState({
    title: '',
    body_html: '',
    vendor: '',
    product_type: '',
    tags: '',
    status: 'draft' as 'active' | 'draft',
    images: [] as string[],
    variants: [{ price: '', sku: '', inventory_quantity: 0 as number | string }],
  });
  const [createProductLoading, setCreateProductLoading] = useState(false);
  const [syncProductsLoading, setSyncProductsLoading] = useState(false);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const [syncCustomersLoading, setSyncCustomersLoading] = useState(false);
  const [customersRefreshKey, setCustomersRefreshKey] = useState(0);
  const [syncOrdersLoading, setSyncOrdersLoading] = useState(false);
  const [ordersRefreshKey, setOrdersRefreshKey] = useState(0);
  const [syncToCatalogLoading, setSyncToCatalogLoading] = useState(false);
  const [syncCheckoutsLoading, setSyncCheckoutsLoading] = useState(false);
  const [checkoutsRefreshKey, setCheckoutsRefreshKey] = useState(0);
  const [searchParams, setSearchParams] = useScopedSearchParams(variant === 'drawer');
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [deleteProductDialogOpen, setDeleteProductDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<{ id: string; shopify_product_id: string; title: string } | null>(null);
  const [deleteProductLoading, setDeleteProductLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useCommerceSyncEvents(workspaceScopeId, (event) => {
    if (event.type === 'products') setProductsRefreshKey((k) => k + 1);
    else if (event.type === 'orders') setOrdersRefreshKey((k) => k + 1);
    else if (event.type === 'customers') setCustomersRefreshKey((k) => k + 1);
    else if (event.type === 'checkouts') setCheckoutsRefreshKey((k) => k + 1);
  });

  const activeTab = searchParams.get('tab') || 'connect';
  const setActiveTab = (tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams();
      newParams.set('tab', tab);
      return newParams;
    });
  };

  const productsPage = parseInt(searchParams.get('page') || '1');
  const setProductsPage = (page: number) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('page', page.toString());
      return newParams;
    });
  };

  const [productsPageSize, setProductsPageSize] = useState(10);
  const [checkoutsPage, setCheckoutsPage] = useState(1);
  const [checkoutsPageSize, setCheckoutsPageSize] = useState(10);

  const productsSearch = searchParams.get('search') || '';
  const setProductsSearch = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value) newParams.set('search', value);
      else newParams.delete('search');
      newParams.set('page', '1'); // Reset page on search
      return newParams;
    });
  };

  const productsStatusFilter = searchParams.get('status') || 'all';
  const setProductsStatusFilter = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value !== 'all') newParams.set('status', value);
      else newParams.delete('status');
      newParams.set('page', '1');
      return newParams;
    });
  };

  const productsVendorFilter = searchParams.get('vendor') || 'all';
  const setProductsVendorFilter = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value !== 'all') newParams.set('vendor', value);
      else newParams.delete('vendor');
      newParams.set('page', '1');
      return newParams;
    });
  };

  const productsTypeFilter = searchParams.get('type') || 'all';
  const setProductsTypeFilter = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (value !== 'all') newParams.set('type', value);
      else newParams.delete('type');
      newParams.set('page', '1');
      return newParams;
    });
  };

  const productsSortBy = searchParams.get('sort') || 'title';
  const setProductsSortBy = (value: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('sort', value);
      return newParams;
    });
  };

  const productsSortOrder = (searchParams.get('order') as 'asc' | 'desc') || 'asc';
  const setProductsSortOrder = (value: 'asc' | 'desc') => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('order', value);
      return newParams;
    });
  };

  const uniqueVendors = useMemo(() => {
    const vendors = new Set(localProducts.map(p => p.vendor).filter(Boolean));
    return Array.from(vendors).sort();
  }, [localProducts]);

  const uniqueTypes = useMemo(() => {
    const types = new Set(localProducts.map(p => p.product_type).filter(Boolean));
    return Array.from(types).sort();
  }, [localProducts]);

  const filteredAndSortedProducts = useMemo(() => {
    let result = [...localProducts];

    if (productsSearch) {
      const searchLower = productsSearch.toLowerCase();
      result = result.filter(p =>
        p.title?.toLowerCase().includes(searchLower) ||
        p.vendor?.toLowerCase().includes(searchLower) ||
        p.product_type?.toLowerCase().includes(searchLower)
      );
    }

    if (productsStatusFilter !== 'all') {
      result = result.filter(p => p.status === productsStatusFilter);
    }
    if (productsVendorFilter !== 'all') {
      result = result.filter(p => p.vendor === productsVendorFilter);
    }
    if (productsTypeFilter !== 'all') {
      result = result.filter(p => p.product_type === productsTypeFilter);
    }

    result.sort((a, b) => {
      let comparison = 0;
      switch (productsSortBy) {
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'price':
          const priceA = parseFloat(a.variants?.[0]?.price || '0');
          const priceB = parseFloat(b.variants?.[0]?.price || '0');
          comparison = priceA - priceB;
          break;
        case 'vendor':
          comparison = (a.vendor || '').localeCompare(b.vendor || '');
          break;
        case 'status':
          comparison = (a.status || '').localeCompare(b.status || '');
          break;
        default:
          comparison = 0;
      }
      return productsSortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [localProducts, productsSearch, productsStatusFilter, productsVendorFilter, productsTypeFilter, productsSortBy, productsSortOrder]);

  useEffect(() => {
    setIsConnected(false);
    setConnectionInfo(null);
    setLocalProducts([]);
    setLocalCheckouts([]);
    setShopCurrency('USD');
    setError(null);
    checkConnection();
  }, [workspaceScopeId, organizationId]);

  const applyStoredConnection = (status: any) => {
    const configured = Boolean(status?.connected || status?.configured);
    if (configured) {
      setIsConnected(true);
      setConnectionInfo({
        shop: status.shop,
        currency: status.currency,
        installedAt: status.installedAt,
        businessId: status.businessId || null,
      });
      setShopCurrency(status.currency || 'USD');
    } else {
      setIsConnected(false);
      setConnectionInfo(null);
      setLocalProducts([]);
      setShopCurrency('USD');
    }
    return configured;
  };

  const checkConnection = async () => {
    const seq = ++connectionCheckSeq.current;
    const isCurrent = () => connectionCheckSeq.current === seq;

    setCheckingConnection(true);
    setVerification(null);

    let configured = false;
    try {
      const stored = await shopifyApi.checkConnection(workspaceScopeId, { probe: false });
      if (!isCurrent()) return;
      configured = applyStoredConnection(stored);
    } catch {
      if (!isCurrent()) return;
      applyStoredConnection(null);
    } finally {
      if (isCurrent()) setCheckingConnection(false);
    }

    if (!configured || !isCurrent()) return;

    setVerification('pending');
    try {
      const probed = await shopifyApi.checkConnection(workspaceScopeId, { probe: true });
      if (!isCurrent()) return;

      if (probed?.connected) {
        applyStoredConnection(probed);
        setVerification('verified');
        return;
      }

      if (probed?.dead) {
        applyStoredConnection(probed);
        setVerification(null);
        return;
      }
      setVerification('unverified');
    } catch {
      if (isCurrent()) setVerification('unverified');
    }
  };




  const handleSyncProducts = async () => {
    if (!workspaceScopeId) {
      toast({
        title: 'Business Account Required for Syncing',
        description: 'To sync products from Shopify, please select a business account. You can view existing products without one.',
        variant: 'default',
      });
      return;
    }
    setSyncProductsLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncProducts(workspaceScopeId);

      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Products sync started in the background. You\'ll get a notification when it finishes.',
        });
      } else {
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Products Synced', description: `Synced ${count} ${count === 1 ? 'product' : 'products'} from Shopify.` }
          : { title: 'Up to date', description: 'No new products to sync from Shopify.' }
        );
        setProductsPage(1);
        setProductsRefreshKey(k => k + 1);
      }
    } catch (e: any) {
      const msg = e.message || 'Failed to sync products';
      setError(friendlyMessage(e, "We couldn't sync your products. Please try again."));
      showApiError(e, { fallback: { title: 'Sync Failed', description: msg } });
    } finally {
      setSyncProductsLoading(false);
    }
  };

  const handleSyncCustomers = async () => {
    if (!workspaceScopeId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync customers.',
        variant: 'default',
      });
      return;
    }
    setSyncCustomersLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncCustomers(workspaceScopeId);

      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Customers sync started in the background.',
        });
      } else {
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Customers Synced', description: `Synced ${count} ${count === 1 ? 'customer' : 'customers'} from Shopify.` }
          : { title: 'Up to date', description: 'No new customers to sync from Shopify.' }
        );
        setCustomersRefreshKey(prev => prev + 1);
      }
    } catch (e: any) {
      const friendly = showApiError(e, { fallback: { title: 'Sync Failed', description: 'Failed to sync customers' } });
      setError(friendly.description);
    } finally {
      setSyncCustomersLoading(false);
    }
  };

  const handleSyncOrders = async () => {
    if (!workspaceScopeId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync orders.',
        variant: 'default',
      });
      return;
    }
    setSyncOrdersLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncOrders(workspaceScopeId);

      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Orders sync started in the background.',
        });
      } else {
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Orders Synced', description: `Synced ${count} ${count === 1 ? 'order' : 'orders'} from Shopify.` }
          : { title: 'Up to date', description: 'No new orders to sync from Shopify.' }
        );
        setOrdersRefreshKey(prev => prev + 1);
      }
    } catch (e: any) {
      const friendly = showApiError(e, { fallback: { title: 'Sync Failed', description: 'Failed to sync orders' } });
      setError(friendly.description);
    } finally {
      setSyncOrdersLoading(false);
    }
  };

  const handleSyncToCatalog = async () => {
    if (!workspaceScopeId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync products to WhatsApp catalog.',
        variant: 'default',
      });
      return;
    }
    if (!isConnected) {
      toast({
        title: 'Shopify Connection Required',
        description: 'Please connect your Shopify store first.',
        variant: 'default',
      });
      return;
    }
    const targetBusinessId = connectionInfo?.businessId || workspaceScopeId;
    if (!targetBusinessId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync Shopify products.',
        variant: 'default',
      });
      return;
    }
    const catalogStatusKnown = typeof catalogInfo !== 'undefined';
    if (catalogStatusKnown && (!catalogInfo || catalogInfo.status !== 'connected')) {
      toast({
        title: 'WhatsApp Catalog Not Connected',
        description: 'Please connect a WhatsApp catalog first. Go to the Commerce page to set up your catalog.',
        variant: 'warning',
      });
      return;
    }
    setSyncToCatalogLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncToCatalog(workspaceScopeId, targetBusinessId, {
        limit: 100,
        skipExisting: true,
      });
      toast({ variant: "success",
        title: 'Catalog Sync Started',
        description:
          res?.message ||
          'Sync started in the background. You will receive a notification when it finishes.',
      });
    } catch (e: any) {
      let errorMsg = 'Failed to sync products to WhatsApp catalog';
      if (e.message) {
        errorMsg = e.message;
      } else if (e.response) {
        try {
          const errorData = await e.response.json();
          errorMsg = errorData.message || errorData.error || errorMsg;
        } catch {
          errorMsg = e.response.statusText || errorMsg;
        }
      }

      if (errorMsg.includes('catalog not connected') || errorMsg.includes('No connected catalog')) {
        errorMsg = 'WhatsApp catalog is not connected. Please go to the Commerce page to set up your catalog.';
      }

      setError(friendlyMessage(errorMsg));
      showApiError(e, { fallback: { title: 'Sync Failed', description: errorMsg } });
    } finally {
      setSyncToCatalogLoading(false);
    }
  };

  const handleSyncCheckouts = async () => {
    if (!workspaceScopeId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to sync checkouts.',
        variant: 'default',
      });
      return;
    }
    setSyncCheckoutsLoading(true);
    setError(null);
    try {
      const res = await shopifyApi.syncCheckouts(workspaceScopeId);

      if (res.status === 'queued') {
        toast({ variant: "success",
          title: 'Sync Started',
          description: res.message || 'Checkouts sync started in the background.',
        });
      } else {
        const count = res.synced || 0;
        toast(count > 0
          ? { title: 'Checkouts Synced', description: `Synced ${count} ${count === 1 ? 'checkout' : 'checkouts'} from Shopify.` }
          : { title: 'Up to date', description: 'No new checkouts to sync from Shopify.' }
        );
        setCheckoutsRefreshKey(prev => prev + 1);
      }
    } catch (e: any) {
      const friendly = showApiError(e, { fallback: { title: 'Sync Failed', description: 'Failed to sync checkouts' } });
      setError(friendly.description);
    } finally {
      setSyncCheckoutsLoading(false);
    }
  };



  const handleCreateProduct = async () => {
    if (!workspaceScopeId) {
      toast({
        title: 'Business Account Required',
        description: 'Please select a business account to create products in Shopify.',
        variant: 'default',
      });
      return;
    }
    if (!createProductForm.title) {
      setError('Product title is required');
      return;
    }
    const priceValue = createProductForm.variants[0]?.price?.trim();
    if (!priceValue) {
      setError('Product price is required');
      return;
    }
    const priceNum = parseFloat(priceValue);
    if (isNaN(priceNum) || priceNum < 0) {
      setError('Please enter a valid price (must be a number >= 0)');
      return;
    }
    setCreateProductLoading(true);
    setError(null);
    try {
      const tagsArray = createProductForm.tags
        ? createProductForm.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];

      const imagesArray = createProductForm.images
        .filter(url => url.trim())
        .map(url => ({ src: url.trim() }));

      const productData = {
        title: createProductForm.title.trim(),
        body_html: createProductForm.body_html.trim() || undefined,
        vendor: createProductForm.vendor.trim() || undefined,
        product_type: createProductForm.product_type.trim() || undefined,
        tags: tagsArray.length > 0 ? tagsArray.join(', ') : undefined,
        status: createProductForm.status,
        images: imagesArray.length > 0 ? imagesArray : undefined,
        variants: [{
          price: priceValue,
          sku: createProductForm.variants[0]?.sku?.trim() || undefined,
          inventory_quantity: createProductForm.variants[0]?.inventory_quantity || 0,
        }],
      };
      await shopifyApi.createProduct(workspaceScopeId, productData);
      toast({ variant: "success",
        title: 'Product Created',
        description: 'Product created successfully in Shopify.',
      });
      setCreateProductOpen(false);
      setCreateProductForm({
        title: '',
        body_html: '',
        vendor: '',
        product_type: '',
        tags: '',
        status: 'draft',
        images: [],
        variants: [{ price: '', sku: '', inventory_quantity: 0 }],
      });
      setProductsPage(1);
      setProductsRefreshKey(k => k + 1);
    } catch (e: any) {
      setError(friendlyMessage(e, "We couldn't create the product. Please try again."));
    } finally {
      setCreateProductLoading(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!workspaceScopeId || !productToDelete) {
      return;
    }
    setDeleteProductLoading(true);
    setError(null);
    try {
      await shopifyApi.deleteProduct(workspaceScopeId, productToDelete.shopify_product_id);
      toast({ variant: "success",
        title: 'Product Deleted',
        description: 'Product deleted successfully from Shopify.',
      });
      setDeleteProductDialogOpen(false);
      setProductToDelete(null);
      setProductsRefreshKey(k => k + 1);
    } catch (e: any) {
      setError(friendlyMessage(e, "We couldn't delete this product. Please try again."));
    } finally {
      setDeleteProductLoading(false);
    }
  };

  const openDeleteDialog = (product: any) => {
    setProductToDelete({
      id: product.id,
      shopify_product_id: product.shopify_product_id,
      title: product.title || 'Untitled Product',
    });
    setDeleteProductDialogOpen(true);
  };

  const productsColumns: Column<typeof filteredAndSortedProducts[0]>[] = useMemo(() => [
    {
      key: 'image',
      header: 'Image',
      render: (_, product) => {
        let images = product.images;
        if (typeof images === 'string') {
          try {
            images = JSON.parse(images);
          } catch (e) {
            images = [];
          }
        }
        if (!Array.isArray(images)) {
          images = [];
        }
        const productImage = images.length > 0 ? images[0].src : null;
        return (
          <>
            {productImage ? (
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded overflow-hidden bg-muted flex items-center justify-center">
                <img
                  src={productImage}
                  alt={product.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded bg-muted flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-6 sm:h-6 text-muted-foreground opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
          </>
        );
      },
      className: 'w-12 sm:w-16',
    },
    {
      key: 'product',
      header: 'Product',
      render: (_, product) => {
        return (
          <div>
            <div className="font-medium text-xs sm:text-sm truncate max-w-[150px] sm:max-w-none">{product.title || 'Untitled'}</div>
            {product.handle && (
              <div className="text-xs text-muted-foreground truncate max-w-[150px] sm:max-w-none">/{product.handle}</div>
            )}
          </div>
        );
      },
      className: 'text-xs sm:text-sm',
    },
    {
      key: 'vendor',
      header: 'Vendor',
      render: (_, product) => {
        return <span className="text-xs sm:text-sm text-muted-foreground">{product.vendor || '—'}</span>;
      },
      className: 'text-xs sm:text-sm text-muted-foreground hidden sm:table-cell',
      headerClassName: 'hidden sm:table-cell',
    },
    {
      key: 'type',
      header: 'Type',
      render: (_, product) => {
        return <span className="text-xs sm:text-sm text-muted-foreground">{product.product_type || '—'}</span>;
      },
      className: 'text-xs sm:text-sm text-muted-foreground hidden md:table-cell',
      headerClassName: 'hidden md:table-cell',
    },
    {
      key: 'price',
      header: 'Price',
      render: (_, product) => {
        let variants = product.variants;
        if (typeof variants === 'string') {
          try {
            variants = JSON.parse(variants);
          } catch (e) {
            variants = [];
          }
        }
        if (!Array.isArray(variants)) {
          variants = [];
        }
        const firstVariant = variants.length > 0 ? variants[0] : null;
        let priceValue: number | null = null;
        if (firstVariant?.price) {
          const priceStr = String(firstVariant.price);
          priceValue = parseFloat(priceStr);
          if (isNaN(priceValue)) {
            priceValue = null;
          }
        }
        return (
          <span className="text-right font-medium text-xs sm:text-sm">
            {priceValue !== null ? formatCurrency(priceValue, shopCurrency) : '—'}
          </span>
        );
      },
      className: 'text-right font-medium text-xs sm:text-sm',
      headerClassName: 'text-right',
    },
    {
      key: 'status',
      header: 'Status',
      render: (_, product) => {
        return (
          <Badge variant={product.status === 'active' ? 'default' : 'secondary'} className="text-xs">
            {product.status || 'draft'}
          </Badge>
        );
      },
      className: 'text-xs sm:text-sm',
    },
    {
      key: 'variants',
      header: 'Variants',
      render: (_, product) => {
        let variants = product.variants;
        if (typeof variants === 'string') {
          try {
            variants = JSON.parse(variants);
          } catch (e) {
            variants = [];
          }
        }
        if (!Array.isArray(variants)) {
          variants = [];
        }
        return (
          <span className="text-right text-xs sm:text-sm text-muted-foreground">
            {variants.length > 0 ? variants.length : 0}
          </span>
        );
      },
      className: 'text-right text-xs sm:text-sm text-muted-foreground hidden lg:table-cell',
      headerClassName: 'text-right hidden lg:table-cell',
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, product) => {
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              openDeleteDialog(product);
            }}
            className="h-7 w-7 sm:h-8 sm:w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        );
      },
      className: 'w-16 sm:w-20',
    },
  ], [localProducts, shopCurrency]);


  const confirmStoreConnected = async (): Promise<boolean> => {
    const normalize = (value: string) =>
      String(value || '')
        .trim()
        .replace(/^https?:\/\//, '')
        .replace(/\/$/, '')
        .replace(/^www\./, '')
        .toLowerCase();

    try {
      clearConnectionCheckCache('shopify:');
      const stored = await shopifyApi.checkConnection(workspaceScopeId, { probe: false });
      if (!stored?.connected && !stored?.configured) return false;
      return normalize(stored.shop) === normalize(manualForm.shop);
    } catch {
      return false;
    }
  };

  const handleManualConnect = async () => {
    if (!manualForm.shop) {
      setError('Shop domain is required');
      return;
    }

    if (connectionMethod === 'token' && !manualForm.accessToken) {
      setError('Access token is required');
      return;
    }

    if (connectionMethod === 'credentials' && (!manualForm.clientId || !manualForm.clientSecret)) {
      setError('Client ID and Client Secret are required');
      return;
    }

    setManualLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_CONFIG.BASE_URL}/shopify/manual-install`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          businessId: workspaceScopeId || null,
          shop: manualForm.shop,
          accessToken: connectionMethod === 'token' ? manualForm.accessToken : undefined,
          clientId: connectionMethod === 'credentials' ? manualForm.clientId : undefined,
          clientSecret: connectionMethod === 'credentials' ? manualForm.clientSecret : undefined,
          apiVersion: '2025-01',
          plan: 'monthly',
          shopCurrency: 'USD',
        }),
      });
      let json: any = null;
      try {
        json = await res.json();
      } catch {
        json = null;
      }

      if (!res.ok || !json?.ok) {
        const outcomeUnknown = !json?.error;
        const connectedAnyway = outcomeUnknown && (await confirmStoreConnected());

        if (!connectedAnyway) {
          const errorMessage = friendlyMessage(json?.error || 'Install failed');
          setError(errorMessage);
          toast({
            title: 'Connection Failed',
            description: errorMessage,
            variant: 'destructive',
          });
          return;
        }

        toast({
          variant: 'success',
          title: 'Shopify Connected',
          description:
            'The connect request did not come back in time, but your store is connected. ' +
            'Products and orders are being pulled in the background.',
        });
      } else if (json?.warning) {
        const deferred = json.warning.code === 'webhook_reconcile_deferred';
        toast({
          title: deferred
            ? 'Connected — finishing live updates'
            : 'Connected — live updates are not wired up',
          description: deferred
            ? `${json.warning.message} This finishes on its own, usually within a minute.`
            : `${json.warning.message} Orders and products will still sync when you refresh or run a sync, ` +
              `but Shopify cannot notify us of changes until this is fixed.`,
          variant: deferred ? 'default' : 'destructive',
        });
      } else {
        toast({ variant: "success",
          title: 'Shopify Connected',
          description: "Connected successfully!",
        });
      }

      setError(null);
      setManualForm({ shop: '', accessToken: '', clientId: '', clientSecret: '' });
      setIsConnected(true);
      setConnectionInfo({ shop: manualForm.shop });
      clearConnectionCheckCache('shopify:');
      await checkConnection();
      if (activeTab === 'connect') {
        setActiveTab('products');
      }
    } catch (e: any) {
      const errorMessage = e.message || 'Failed to connect manually';
      setError(friendlyMessage(e, "We couldn't connect Shopify. Please check your details and try again."));
      showApiError(e, { fallback: { title: 'Connection Failed', description: errorMessage } });
    } finally {
      setManualLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (disconnectLoading) return;
    setDisconnectLoading(true);
    setError(null);
    try {
      await shopifyApi.disconnect(workspaceScopeId || connectionInfo?.businessId || null);
      toast({ variant: "success",
        title: 'Shopify Disconnected',
        description: 'The Shopify connection has been removed.',
      });
      setIsConnected(false);
      setConnectionInfo(null);
      setShopCurrency('USD');
      setLocalProducts([]);
      setActiveTab('connect');
    } catch (e: any) {
      setError(friendlyMessage(e, "We couldn't disconnect Shopify. Please try again."));
    } finally {
      setDisconnectLoading(false);
      setDisconnectDialogOpen(false);
    }
  };

  if (accountsLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="p-6 text-center">
            <Loader2 className="h-12 w-12 mx-auto animate-spin mb-4" />
            <h3 className="text-lg font-semibold mb-2">Loading Business Accounts</h3>
            <p className="text-muted-foreground mb-4">
              Please wait while we load your business accounts...
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const renderTabActions = () => {
    if (!isConnected) {
      return null;
    }

    if (activeTab === 'customers') return (
      <div className="flex items-center gap-2">
        <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncCustomers} disabled={syncCustomersLoading}>
          {syncCustomersLoading ? 'Syncing…' : 'Sync from Shopify'}
        </Button>
      </div>
    );

    if (activeTab === 'orders') {
      return (
        <div className="flex items-center gap-2">
          <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncOrders} disabled={syncOrdersLoading}>
            {syncOrdersLoading ? 'Syncing…' : 'Sync from Shopify'}
          </Button>
        </div>
      );
    }

    if (activeTab === 'products') {
      return (
        <div className="flex items-center gap-2">
          <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncProducts} disabled={syncProductsLoading}>
            {syncProductsLoading ? 'Syncing…' : 'Sync from Shopify'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className='text-xs sm:text-sm'
            onClick={handleSyncToCatalog}
            disabled={syncToCatalogLoading || !workspaceScopeId}
          >
            {syncToCatalogLoading ? 'Syncing to Catalog…' : 'Sync to WhatsApp Catalog'}
          </Button>
          <Button variant="default" size="sm" className="text-xs sm:text-sm" onClick={() => setCreateProductOpen(true)}>
            Create Product
          </Button>
          <CreateShopifyProductDialog
            open={createProductOpen}
            onOpenChange={setCreateProductOpen}
            selectedAccountId={workspaceScopeId}
            shopCurrency={shopCurrency}
            onSuccess={() => setProductsRefreshKey(k => k + 1)}
          />
        </div>
      );
    }
    if (activeTab === 'checkouts') {
      return (
        <div className="flex items-center gap-2">
          <Button size="sm" className='text-xs sm:text-sm' onClick={handleSyncCheckouts} disabled={syncCheckoutsLoading}>
            {syncCheckoutsLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                Sync Checkouts
              </>
            )}
          </Button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className=" space-y-4 sm:space-y-6">


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        {variant === 'page' ? (
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/home/integrations')}
              className="h-8 w-8 sm:h-9 sm:w-9"
            >
              <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            </Button>
            <div>
              <CardTitle className="text-xl sm:text-2xl">Shopify Integration</CardTitle>
              <CardDescription className="text-sm">Connect your Shopify shop and manage products & orders</CardDescription>
            </div>
          </div>
        ) : null}
        {checkingConnection ? (
          <Badge variant="outline">Loading…</Badge>
        ) : verification === 'pending' ? (
          <Badge variant="outline">Verifying with Shopify…</Badge>
        ) : verification === 'unverified' ? (
          <Badge
            variant="outline"
            className="border-amber-300 text-amber-700"
            title="Your store is connected and its data is up to date here. Shopify did not answer the live check just now."
          >
            Could not verify with Shopify
          </Badge>
        ) : null}
      </div>


      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className={`flex flex-col gap-3 mb-4 ${variant === 'page' ? 'sm:flex-row sm:items-center sm:justify-between' : ''}`}>
          <div className="flex min-w-0 overflow-x-auto -mx-2 px-2">
            <TabsList className="inline-flex gap-2 p-2 h-[46px] rounded-[20px]">
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="connect">
                <span className="relative z-10">Connect Shop</span>
                {activeTab === 'connect' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="products" disabled={!isConnected}>
                <span className="relative z-10">Products</span>
                {activeTab === 'products' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="customers" disabled={!isConnected}>
                <span className="relative z-10">Customers</span>
                {activeTab === 'customers' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="orders" disabled={!isConnected}>
                <span className="relative z-10">Orders</span>
                {activeTab === 'orders' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="checkouts" disabled={!isConnected}>
                <span className="relative z-10">Abandoned</span>
                {activeTab === 'checkouts' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger
                className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none'
                value="playbooks"
                disabled={!isConnected}
                data-testid="shopify-tab-playbooks"
              >
                <span className="relative z-10">Auto Messages</span>
                {activeTab === 'playbooks' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
              <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="configuration" disabled={!isConnected}>
                <span className="relative z-10">Configuration</span>
                {activeTab === 'configuration' && (
                  <motion.div
                    layoutId="shopify-tab-pill"
                    className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                    transition={{ type: "spring", bounce: 0.2, duration: 0.5 }}
                  />
                )}
              </TabsTrigger>
            </TabsList>
          </div>
          <div className="flex shrink-0 items-center gap-2">{renderTabActions()}</div>
        </div>

        <TabsContent value="connect" className="space-y-4">
          {checkingConnection ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : isConnected ? (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/20 p-4 sm:p-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Connected shop</p>
                    <p className="text-xl font-semibold">{connectionInfo?.shop}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm">
                          Disconnect
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Disconnect Shopify</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will remove the current Shopify connection. You can reconnect at any time, but product and order syncing will stop until you do.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel disabled={disconnectLoading}>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDisconnect}
                            disabled={disconnectLoading}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            {disconnectLoading ? 'Disconnecting…' : 'Disconnect'}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="mt-6 grid gap-4 sm:grid-cols-3 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Connected on</p>
                    <p className="font-medium">
                      {connectionInfo?.installedAt ? new Date(connectionInfo.installedAt).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Currency</p>
                    <p className="font-medium">{connectionInfo?.currency || shopCurrency}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">Status</p>
                    <p className="font-medium text-green-600 dark:text-green-400">Connected</p>
                  </div>
                </div>
                <div className="mt-4">
                  <Button variant="outline" size="sm" onClick={() => setActiveTab('products')}>
                    Manage Products
                  </Button>
                </div>
              </div>
              {error && (
                <div className="text-xs sm:text-sm text-destructive p-2 sm:p-3 bg-destructive/10 rounded">
                  {error}
                </div>
              )}
            </div>
          ) : (
            <>
              <h3 className="text-lg font-semibold mb-4">Connect Your Shopify Shop</h3>
              <div className="space-y-4">
                <div className="flex p-1 bg-muted rounded-lg w-fit">
                  <button
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${connectionMethod === 'credentials'
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                      }`}
                    onClick={() => setConnectionMethod('credentials')}
                  >
                    New (Client Credentials)
                  </button>
                  <button
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${connectionMethod === 'token'
                      ? 'bg-background shadow-sm text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                      }`}
                    onClick={() => setConnectionMethod('token')}
                  >
                    Classic (Access Token)
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="shop">Shop Domain *</Label>
                    <Input
                      id="shop"
                      placeholder="cool-store.myshopify.com"
                      value={manualForm.shop}
                      onChange={(e) => setManualForm({ ...manualForm, shop: e.target.value })}
                    />
                  </div>

                  {connectionMethod === 'token' ? (
                    <div className="space-y-2">
                      <Label htmlFor="accessToken">Access Token *</Label>
                      <Input
                        id="accessToken"
                        type="password"
                        placeholder="shpat_..."
                        value={manualForm.accessToken}
                        onChange={(e) => setManualForm({ ...manualForm, accessToken: e.target.value })}
                      />
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="clientId">Client ID *</Label>
                        <Input
                          id="clientId"
                          placeholder="Shoipify Client ID"
                          value={manualForm.clientId}
                          onChange={(e) => setManualForm({ ...manualForm, clientId: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clientSecret">Client Secret *</Label>
                        <Input
                          id="clientSecret"
                          type="password"
                          placeholder="Shopify Client Secret"
                          value={manualForm.clientSecret}
                          onChange={(e) => setManualForm({ ...manualForm, clientSecret: e.target.value })}
                        />
                      </div>
                    </>
                  )}
                </div>

                <p className="text-sm text-muted-foreground">
                  API version defaults to 2025-01. Plan and currency can be updated later.
                </p>

                {connectionMethod === 'token' && (
                  <p className="text-xs text-muted-foreground">
                    Need credentials? In Shopify admin go to <span className="font-medium">Settings → Apps &amp; sales channels → Develop apps</span>,
                    create (or select) a custom app, then add the <span className="font-medium">read_products</span>, <span className="font-medium">write_products</span>,
                    <span className="font-medium">read_orders</span>, <span className="font-medium">write_orders</span>, <span className="font-medium">read_customers</span>, <span className="font-medium">write_customers</span>,
                    <span className="font-medium">read_checkouts</span>, <span className="font-medium">read_inventory</span>, <span className="font-medium">write_inventory</span>,
                    <span className="font-medium">read_discounts</span>, <span className="font-medium">write_discounts</span>, <span className="font-medium">read_draft_orders</span>, and <span className="font-medium">write_draft_orders</span> scopes to generate an Admin API access token.
                  </p>
                )}

                {connectionMethod === 'token' && (
                  <p className="text-xs text-muted-foreground">
                    To import orders older than 60 days you also need{' '}
                    <span className="font-medium">read_all_orders</span>, which Shopify
                    grants on request. Without it the Orders API returns only the last
                    60 days — everything syncs, there is just less of it than your
                    Shopify admin shows.
                  </p>
                )}

                {connectionMethod === 'credentials' && (
                  <p className="text-xs text-muted-foreground">
                    Use <strong>Client Credentials</strong> for automatic token refreshing.
                    Tokens generated this way expire in 24h, but we will auto-refresh them for you.
                  </p>
                )}

                <Button size="sm" onClick={handleManualConnect} disabled={manualLoading}>
                  {manualLoading ? 'Connecting…' : 'Connect Manually'}
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="configuration" className="space-y-4 pt-4">
          <ShopifyConfiguration
            businessAccountId={workspaceScopeId}
            active={activeTab === 'configuration' && isConnected}
          />
        </TabsContent>

        <TabsContent value="products" className="space-y-4 pt-4">
          <ShopifyProducts
            selectedAccountId={workspaceScopeId}
            isConnected={isConnected}
            shopCurrency={shopCurrency}
            refreshKey={productsRefreshKey}
            hideToolbar={true}
          />
        </TabsContent>

        <TabsContent value="customers" className="space-y-4 pt-4">
          <ShopifyCustomers
            selectedAccountId={workspaceScopeId}
            isConnected={isConnected}
            refreshKey={customersRefreshKey}
          />
        </TabsContent>

        <TabsContent value="orders" className="space-y-4 pt-4">
          <ShopifyOrders
            selectedAccountId={workspaceScopeId}
            isConnected={isConnected}
            shopCurrency={shopCurrency}
            shopDomain={connectionInfo?.shop}
            refreshKey={ordersRefreshKey}
          />
        </TabsContent>

        <TabsContent value="checkouts" className="space-y-4 pt-4">
          <ShopifyCheckouts
            selectedAccountId={workspaceScopeId}
            isConnected={isConnected}
            shopCurrency={shopCurrency}
            refreshKey={checkoutsRefreshKey}
          />
        </TabsContent>

        <TabsContent value="playbooks" className="space-y-4 pt-4">
          <ShopifyPlaybooks
            businessAccountId={workspaceScopeId || ''}
            isConnected={isConnected}
          />
        </TabsContent>
      </Tabs >

    </div >
  );
}
